import React from 'react';
import { ProposalState } from '../../models/clinicAgent';
import { proposalTitle, isApplicable } from '../../services/clinicAgentProposals';
import { ProposalPayloadView } from './ProposalPayload';

interface ProposalCardProps {
  proposal: ProposalState;
  onConfirm: () => void;
  onDiscard: () => void;
}

// An agent proposal for the patient's clinical context: the text in full, and
// who will see it once accepted.
const ContextProposalPayload: React.FC<{ payload: Record<string, unknown> }> = ({ payload }) => (
  <div style={{ marginTop: 8 }}>
    <div
      style={{
        fontSize: 13,
        whiteSpace: 'pre-wrap',
        background: 'var(--muted)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '8px 10px',
        color: 'var(--foreground)',
      }}
    >
      {String(payload.text ?? '')}
    </div>
    <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 6 }}>
      {payload.mode === 'replace'
        ? 'Sustituye todo el contexto clínico actual. Lo verá el equipo clínico del paciente; el paciente no.'
        : 'Se añade al final del contexto clínico. Lo verá el equipo clínico del paciente; el paciente no.'}
    </div>
  </div>
);

const ProposalCard: React.FC<ProposalCardProps> = ({ proposal, onConfirm, onDiscard }) => {
  const applicable = isApplicable(proposal.action);
  const { status } = proposal;

  // Accepting a diary item with inline new_meals also creates those meals in the
  // patient's library; the resolve detail reports them, so the applied badge says
  // so rather than leaving the clinician to check the library.
  const createdMeals = Array.isArray(proposal.detail?.created_meal_ids)
    ? (proposal.detail!.created_meal_ids as unknown[]).length
    : 0;

  // An accepted context proposal reports the version it created.
  const contextVersion =
    proposal.action === 'update_patient_context' && proposal.detail?.version != null
      ? Number(proposal.detail.version)
      : null;

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderLeft: '3px solid var(--primary)',
        borderRadius: 10,
        background: 'var(--card)',
        padding: 12,
        marginTop: 8,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--foreground)' }}>
        {proposalTitle(proposal.action, proposal.payload)}
      </div>
      {proposal.rationale && (
        <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 4, fontStyle: 'italic' }}>
          {proposal.rationale}
        </div>
      )}
      {proposal.action === 'update_patient_context' ? (
        <ContextProposalPayload payload={proposal.payload || {}} />
      ) : (
        <ProposalPayloadView payload={proposal.payload || {}} />
      )}

      {!applicable && (
        <div style={{ fontSize: 12, color: 'var(--warning)', marginTop: 8 }}>
          Acción no reconocida — solo lectura.
        </div>
      )}

      {status === 'error' && proposal.error && (
        <div style={{ fontSize: 12, color: 'var(--destructive)', marginTop: 8 }}>{proposal.error}</div>
      )}

      {applicable && status !== 'applied' && status !== 'discarded' && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button
            onClick={onConfirm}
            disabled={status === 'applying'}
            style={{
              border: 'none',
              borderRadius: 8,
              padding: '6px 14px',
              background: status === 'applying' ? 'var(--primary-subtle)' : 'var(--primary)',
              color: 'var(--card)',
              fontWeight: 600,
              fontSize: 13,
              cursor: status === 'applying' ? 'default' : 'pointer',
            }}
          >
            {status === 'applying' ? 'Aplicando…' : status === 'error' ? 'Reintentar' : 'Confirmar'}
          </button>
          <button
            onClick={onDiscard}
            disabled={status === 'applying'}
            style={{
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '6px 14px',
              background: 'var(--card)',
              color: 'var(--muted-foreground)',
              fontWeight: 600,
              fontSize: 13,
              cursor: status === 'applying' ? 'default' : 'pointer',
            }}
          >
            Descartar
          </button>
        </div>
      )}

      {status === 'applied' && (
        <div style={{ fontSize: 12, color: 'var(--success)', marginTop: 10, fontWeight: 600 }}>
          ✓ Aplicado
          {contextVersion != null && ` · contexto clínico en la versión ${contextVersion}`}
          {createdMeals > 0 &&
            ` · ${createdMeals} ${createdMeals === 1 ? 'comida creada' : 'comidas creadas'}`}
        </div>
      )}

      {status === 'discarded' && (
        <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 10, fontWeight: 600 }}>
          Descartado
        </div>
      )}
    </div>
  );
};

export default ProposalCard;
